import type {AxiosRequestConfig} from "axios";

import HttpClient from "api/index";

import {
	TCompanyMapRequestBody,
	TCompanyModeResponse,
	TFilterCompanyPayload,
	TOrganizationalChartParams,
	TReportParams,
	TSearchCompanyApiPayload,
} from "api/companyApi/types";
import {cleanPayloadObject} from "helpers/filterHandlers";
import {TPaginationParam} from "../candidateApi/types";

class MapXCompanyApi extends HttpClient {
	async getCompany(companyID: string, config = {}) {
		return this.doGet(`/companies/${companyID}`, config);
	}

	async getCompanyMode(anchorCompanyIds: number[], config = {}) {
		const payload = {filters: {anchor_company_ids: anchorCompanyIds}};

		return this.doPost<TCompanyModeResponse, typeof payload>(
			`/companies/modes`,
			payload,
			config,
		);
	}

	async getCompanyLLMSuggestions(description: string, config: AxiosRequestConfig) {
		const payload = {
			user_input: description,
		};

		return this.doPost(`/get-company-llm-suggestions`, payload, config);
	}

	async getCompanyByName(name: string, config: AxiosRequestConfig) {
		const payload = {
			filters: {
				name,
			},
			pagination: {
				page: 1,
				per_page: 40,
			},
		};

		return this.doPost(`/companies`, payload, config);
	}

	async getCompanyLocationReport(companyId: number, payload: TReportParams, config = {}) {
		const cleanedPayload = cleanPayloadObject(payload);

		return this.doPost(
			`/companies/${companyId}/locations-report`,
			{filters: cleanedPayload},
			config,
		);
	}

	async fetchCompanies(pagination: TPaginationParam, config = {}) {
		const apiPayload = {pagination};

		return this.doPost(`/companies`, apiPayload, config);
	}

	async fetchCompaniesByFilter(payload: TFilterCompanyPayload, config = {}) {
		return this.doPost(`/companies`, payload, config);
	}

	async searchCompaniesByFilter(payload: TSearchCompanyApiPayload, config = {}) {
		return this.doPost(`/companies`, payload, config);
	}

	async getCompaniesByCompanyIds(companies: number[], pagination: TPaginationParam, config = {}) {
		return this.doPost(`/companies`, {filters: {companies}, pagination}, config);
	}

	async getCompaniesByFilter(filterQuery: string, config = {}) {
		return this.doGet(`/companies${filterQuery}`, config);
	}

	async getSimilarCompanies(filterQuery: string, perPage = 3, config: AxiosRequestConfig) {
		filterQuery = filterQuery ? filterQuery + `&per_page=${perPage}` : `?per_page=${perPage}`;

		return this.doGet(`/mapx/similar-companies${filterQuery}`, config);
	}

	async getCompanyMapFunctionHistory(companyId: string, config?: AxiosRequestConfig) {
		return this.doGet(`/map-company-requests`, {
			...config,
			params: {
				company_id: companyId,
				per_page: 20,
			},
		});
	}

	async saveCompanyMapRequest(formBody: TCompanyMapRequestBody, config?: AxiosRequestConfig) {
		return this.doPost(`/map-company-requests`, formBody, config);
	}

	async getCompanyMapFunctionById(requestId: string, config?: AxiosRequestConfig) {
		return this.doGet(`/map-company-requests/${requestId}`, config);
	}

	async getCompaniesRevenueDistribution(params: string) {
		return this.doGet(`/mapx/companies-revenue-distribution${params}`);
	}

	async getCompaniesSizeDistribution(params: string) {
		return this.doGet(`/mapx/companies-size-distribution${params}`);
	}

	async getCompanyEthnicDiversity(companyId: string, params: TReportParams = {}, config = {}) {
		const cleanedPayload = cleanPayloadObject(params);

		return this.doPost(
			`/companies/${companyId}/ethnic-diversity-report`,
			{filters: cleanedPayload},
			config,
		);
	}

	async getCompanyGenderDiversity(companyId: string, params: TReportParams = {}, config = {}) {
		const cleanedPayload = cleanPayloadObject(params);

		return this.doPost(
			`/companies/${companyId}/gender-diversity-report`,
			{filters: cleanedPayload},
			config,
		);
	}

	async getCompanyTrendStats(companyId: string, params: TReportParams = {}, config = {}) {
		const cleanedPayload = cleanPayloadObject(params);

		return this.doPost(`/companies/${companyId}/stats`, {filters: cleanedPayload}, config);
	}

	async getCompanyOrganizationalChart(
		companyId: string,
		params: TReportParams = {},
		config = {},
	) {
		const organizationChartParams: TOrganizationalChartParams = {...params};

		if (organizationChartParams?.regions && organizationChartParams?.regions?.length > 0) {
			delete organizationChartParams.regions;
		}

		let pagination = {page: 1, per_page: 40};

		if (organizationChartParams && !("page" in organizationChartParams)) {
			pagination = {page: 1, per_page: 40};
		} else if (organizationChartParams && "page" in organizationChartParams) {
			pagination = {
				page: organizationChartParams.page || 1, // even though its page 1, BE skipped 1st 40
				per_page: organizationChartParams.per_page || 1000,
			};
		}

		delete organizationChartParams.page;
		delete organizationChartParams.per_page;

		const cleanedPayload = cleanPayloadObject(organizationChartParams);

		return await this.doPost(
			`/companies/${companyId}/organisation-chart`,
			{filters: cleanedPayload, pagination},
			config,
		);
	}

	async getCompanyFrequentlyHiredFrom(
		companyId: string,
		page = 1,
		params: TReportParams = {},
		config?: AxiosRequestConfig,
	) {
		const pagination = {
			page,
			per_page: 9,
		};
		const cleanedPayload = cleanPayloadObject(params);

		return this.doPost(
			`/companies/${companyId}/frequently-hired-from`,
			{filters: cleanedPayload, pagination},
			config,
		);
	}

	async getCompanyIndustryBackground(
		companyId: string,
		params: TReportParams = {},
		config?: AxiosRequestConfig,
	) {
		const cleanedPayload = cleanPayloadObject(params);

		return this.doPost(
			`/companies/${companyId}/industries-background-report`,
			{filters: cleanedPayload},
			config,
		);
	}

	async getCompanyFunctionalBackground(
		companyId: string,
		params: TReportParams = {},
		config?: AxiosRequestConfig,
	) {
		const cleanedPayload = cleanPayloadObject(params);

		return this.doPost(
			`/companies/${companyId}/specialism-positions-report`,
			{filters: cleanedPayload},
			config,
		);
	}
}

const mapXCompanyApi = new MapXCompanyApi();

export default mapXCompanyApi;
